import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyNoteRequest, resolveNoteRequest, cancelNoteRequest } from '../api';
import { noteRequestKeys } from '../hooks/useNoteRequests';
import { inventoryKeys } from '../hooks/useInventory';
import { secRow, pluralize } from './NoteRequestBits';

// Bulk actions over the requests a pricer ticked in the Queue / Waiting tabs.
// One modal, three actions:
//   apply   — runs the guarded writer for every selected request that has a
//             pending/failed listing, ONE REQUEST AT A TIME (the server paces
//             the Skybox calls 1.1 s apart, so a big batch is deliberately slow)
//   resolve — mark handled without writing (optional note applies to all)
//   cancel  — cancel every selected open request
// Results render per request and the modal stays open until closed so a
// failed write can't scroll away. Nothing here bypasses the per-request
// endpoints — it just calls them in sequence.
//
// Props: action ('apply'|'resolve'|'cancel'), requests (RequestView[]),
//        enabled (note sync master switch), onClose(), onMessage(msg, isError)
const APPLIABLE = new Set(['pending', 'failed']);

const TITLES = {
  apply: 'Apply selected requests',
  resolve: 'Resolve selected requests',
  cancel: 'Cancel selected requests',
};

function pendingCount(req) {
  return (req.listings || []).filter(l => APPLIABLE.has(l.state)).length;
}

function NoteRequestBulkModal({ action, requests, enabled, onClose, onMessage }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState({}); // request id -> { ok, text, bad }
  const [current, setCurrent] = useState(null);

  const eligible = action === 'apply' ? requests.filter(r => pendingCount(r) > 0) : requests;
  const skipped = requests.length - eligible.length;
  const totalListings = eligible.reduce((n, r) => n + pendingCount(r), 0);
  const anyTags = eligible.some(r => (r.tags || []).length > 0);

  const close = () => {
    if (running) return; // don't lose the in-flight batch's results
    queryClient.invalidateQueries({ queryKey: noteRequestKeys.all });
    if (action === 'apply') queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    onClose?.();
  };

  const run = async () => {
    if (!eligible.length || running) return;
    setRunning(true);
    let okCount = 0; let badCount = 0;
    for (const req of eligible) {
      setCurrent(req.id);
      try {
        if (action === 'apply') {
          const res = await applyNoteRequest(req.id, {});
          const rs = res.data.results || [];
          const applied = rs.filter(r => r.state === 'applied').length;
          const verified = rs.filter(r => r.state === 'verified').length;
          const failed = rs.filter(r => r.state === 'failed');
          const other = rs.length - applied - verified - failed.length;
          const parts = [];
          if (applied) parts.push(`${applied} written`);
          if (verified) parts.push(`${verified} already had it`);
          if (other) parts.push(`${other} skipped`);
          if (failed.length) parts.push(`${failed.length} failed: ${failed.map(f => f.error).filter(Boolean).slice(0, 2).join('; ')}`);
          const bad = failed.length > 0;
          setResults(prev => ({ ...prev, [req.id]: { ok: !bad, bad, text: parts.join(' · ') || 'nothing to do' } }));
          bad ? badCount++ : okCount++;
        } else if (action === 'resolve') {
          await resolveNoteRequest(req.id, note.trim() || undefined);
          setResults(prev => ({ ...prev, [req.id]: { ok: true, text: 'resolved' } }));
          okCount++;
        } else {
          await cancelNoteRequest(req.id);
          setResults(prev => ({ ...prev, [req.id]: { ok: true, text: 'cancelled' } }));
          okCount++;
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'failed';
        setResults(prev => ({ ...prev, [req.id]: { ok: false, bad: true, text: msg } }));
        badCount++;
        // A dead master switch or dead Skybox token fails every request the same way — stop early
        const code = err.response?.data?.code;
        if (code === 'DISABLED' || err.response?.status === 503) break;
      }
    }
    setCurrent(null);
    setRunning(false);
    setDone(true);
    queryClient.invalidateQueries({ queryKey: noteRequestKeys.all });
    if (action === 'apply') queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    onMessage?.(`${TITLES[action]}: ${okCount} ok${badCount ? `, ${badCount} failed` : ''}`, badCount > 0);
  };

  const confirmLabel = action === 'apply'
    ? `Apply ${pluralize(eligible.length, 'request')} (${pluralize(totalListings, 'listing')})`
    : action === 'resolve' ? `Resolve ${pluralize(eligible.length, 'request')}` : `Cancel ${pluralize(eligible.length, 'request')}`;

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal modal-content" style={{ maxWidth: 860, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{TITLES[action]}</h3>
          <button className="modal-close" onClick={close} disabled={running}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          {action === 'apply' && enabled === false && (
            <div className="message message-error">Note sync is off — turn it on in Admin → Note Sync before applying.</div>
          )}
          {action === 'apply' && (
            <p className="nr-muted" style={{ marginTop: 0 }}>
              Writes the requested note(s){anyTags ? ' and tags' : ''} to every pending listing of each request below, one request at a time (Skybox calls are paced ~1 s apart, so a big batch takes a while). Listings that already contain the phrase are marked verified without a write{anyTags ? ', unless the request also asks for tags (those are always added)' : ''}.
              {skipped > 0 ? ` ${pluralize(skipped, 'selected request')} without a pending listing will be skipped.` : ''}
            </p>
          )}
          {action === 'resolve' && (
            <div className="form-group">
              <label className="form-label">Optional note for the record (applied to all)</label>
              <input className="form-input" value={note} onChange={e => setNote(e.target.value)} maxLength={200} placeholder="e.g. fixed directly in Skybox" disabled={running || done} />
            </div>
          )}
          {action === 'cancel' && (
            <p className="nr-muted" style={{ marginTop: 0 }}>Cancels every selected open request. Buyers see them as Cancelled; nothing is written.</p>
          )}

          <table className="nr-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>#</th><th>Order #</th><th>Buyer</th><th>Listings</th><th>Result</th></tr>
            </thead>
            <tbody>
              {requests.map(req => {
                const r = results[req.id];
                const isEligible = eligible.includes(req);
                return (
                  <tr key={req.id}>
                    <td className="nr-mono">{req.id}</td>
                    <td className="nr-mono nr-order">{req.orderNumber}</td>
                    <td>{req.submittedByName || '—'}</td>
                    <td>{(req.listings || []).length ? req.listings.map(secRow).join(' · ') : <span className="nr-muted">—</span>}{action === 'apply' ? <span className="nr-muted"> · {pendingCount(req)} pending</span> : null}</td>
                    <td>
                      {!isEligible ? <span className="nr-result-muted">skipped — nothing pending</span>
                        : current === req.id ? <span className="nr-muted">working…</span>
                        : r ? <span className={r.bad ? 'nr-result-bad' : 'nr-result-ok'}>{r.bad ? '✗ ' : '✓ '}{r.text}</span>
                        : <span className="nr-muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>
          <span className="nr-muted" style={{ flex: 1 }}>{pluralize(requests.length, 'request')} selected{action === 'apply' ? ` · ${pluralize(totalListings, 'pending listing')}` : ''}</span>
          <button className="btn btn-secondary btn-sm" onClick={close} disabled={running}>{done ? 'Close' : 'Cancel'}</button>
          {!done && (
            <button
              className={`btn ${action === 'cancel' ? 'btn-danger' : 'btn-primary'} btn-sm`}
              onClick={run}
              disabled={running || !eligible.length || (action === 'apply' && enabled === false)}
            >
              {running ? 'Working…' : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default NoteRequestBulkModal;
